import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { ActivityDetail } from '../../types/activity';
import { useUpdateActivity, useUpdateActivityStatus } from '../../hooks/useActivities';
import ActivityActionBar from './ActivityActionBar';
import ArchiveConfirmationModal from './ArchiveConfirmationModal';
import UnsavedChangesModal from './UnsavedChangesModal';
import ActivityDetailHeader from './ActivityDetailHeader';
import ActivityFormFields, { type ActivityImageValue } from './ActivityFormFields';
import { activityDetailToForm } from './activityForm';
import { useActivityForm } from './useActivityForm';

interface ActivityEditPanelProps {
  activity: ActivityDetail;
  currentIndex: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  onArchived?: () => void;
}

const imageOf = (a: ActivityDetail): ActivityImageValue | null =>
  a.imageUrl && a.imageFileId ? { documentId: a.imageFileId, viewUrl: a.imageUrl } : null;

export default function ActivityEditPanel({
  activity,
  currentIndex,
  total,
  onPrev,
  onNext,
  onDirtyChange,
  onArchived,
}: ActivityEditPanelProps) {
  const form = useActivityForm(() => activityDetailToForm(activity), onDirtyChange);
  const [pendingNav, setPendingNav] = useState<(() => void) | null>(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);

  const updateMutation = useUpdateActivity();
  const statusMutation = useUpdateActivityStatus();

  const { reset, isDirty } = form;
  const loadedRef = useRef<{ id: number; updatedAt: string | null } | null>(null);

  // Reload the form for a different activity, or when the stored data changes (e.g. after a save,
  // so new point ids are picked up and a second save doesn't duplicate points) while not dirty.
  useEffect(() => {
    const loaded = loadedRef.current;
    const isNewActivity = !loaded || loaded.id !== activity.id;
    const isRefreshed = loaded?.updatedAt !== activity.updatedAt && !isDirty;
    if (isNewActivity || isRefreshed) {
      reset(activityDetailToForm(activity), imageOf(activity));
      loadedRef.current = { id: activity.id, updatedAt: activity.updatedAt };
    }
  }, [activity, isDirty, reset]);

  const isArchived = activity.status === 'archived';
  const canGoLive = activity.allowedTransitions.includes('published');
  const canUnpublish = activity.allowedTransitions.includes('unpublished');
  const canArchive = activity.allowedTransitions.includes('archived');

  const handleSave = async () => {
    const payload = form.validate();
    if (!payload) return;
    try {
      await updateMutation.mutateAsync({ id: activity.id, data: payload });
      form.setIsDirty(false);
    } catch (err) {
      form.handleServerError(err, payload);
    }
  };

  const handleStatusTransition = async () => {
    if (activity.status === 'published') {
      if (canUnpublish) statusMutation.mutate({ id: activity.id, status: 'unpublished' });
      return;
    }
    if (!canGoLive) return;
    // The backend publishes the stored data, so unsaved edits would be ignored
    if (form.isDirty) {
      toast.error('Save your changes before going live');
      return;
    }
    const payload = form.validate();
    if (!payload) return;
    try {
      await statusMutation.mutateAsync({ id: activity.id, status: 'published' });
    } catch (err) {
      form.handleServerError(err, payload);
    }
  };

  const handleArchiveConfirm = () => {
    statusMutation.mutate(
      { id: activity.id, status: 'archived' },
      {
        onSuccess: () => {
          setShowArchiveModal(false);
          onArchived?.();
        },
      },
    );
  };

  const guardedNav = (cb: () => void) => {
    if (form.isDirty) {
      setPendingNav(() => cb);
    } else {
      cb();
    }
  };

  const handlePrev = () => guardedNav(onPrev);
  const handleNext = () => guardedNav(onNext);

  return (
    <>
      <div data-testid="activity-edit-panel" className="admin-card p-[30px] relative">
        <ActivityDetailHeader
          currentIndex={currentIndex}
          total={total}
          activityKey={activity.key}
          status={activity.status}
          onPrev={handlePrev}
          onNext={handleNext}
        />

        {isArchived && (
          <p
            data-testid="archived-notice"
            className="mb-4 px-3 py-2 text-[13px] rounded-md bg-pine-hover-item text-pine-ink-soft"
          >
            Archived activities can't be edited
          </p>
        )}

        <ActivityFormFields
          formData={form.formData}
          onFieldChange={form.onFieldChange}
          onLevelChange={form.onLevelChange}
          errors={form.errors}
          imageValue={form.imageValue}
          onImageChange={form.onImageChange}
          readOnly={isArchived}
        />

        <ActivityActionBar
          onSave={handleSave}
          onStatusTransition={handleStatusTransition}
          onArchive={() => setShowArchiveModal(true)}
          onPrev={handlePrev}
          onNext={handleNext}
          isSaving={updateMutation.isPending}
          isArchiving={statusMutation.isPending}
          isDirty={form.isDirty}
          status={activity.status}
          currentIndex={currentIndex}
          total={total}
          canTransition={activity.status === 'published' ? canUnpublish : canGoLive}
          canArchive={canArchive}
          hideSave={isArchived}
        />
      </div>

      <UnsavedChangesModal
        open={pendingNav !== null}
        onLeave={() => {
          const nav = pendingNav;
          setPendingNav(null);
          form.setIsDirty(false);
          nav?.();
        }}
        onStay={() => setPendingNav(null)}
      />

      <ArchiveConfirmationModal
        open={showArchiveModal}
        onClose={() => setShowArchiveModal(false)}
        onConfirm={handleArchiveConfirm}
        isLoading={statusMutation.isPending}
      />
    </>
  );
}
